import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Upload, X, Check, MessageCircle, Handshake, Package } from 'lucide-react';
import { toast } from 'sonner';

const PRICE_RANGES = [
  { id: 'up_to_5000', label: 'Up to ₹5,000', minPieces: 20 },
  { id: '5000_to_10000', label: '₹5,000 – ₹10,000', minPieces: 10 },
  { id: 'above_10000', label: 'Above ₹10,000', minPieces: 5 }
];

const Wholesale = () => {
  const [form, setForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    message: '',
    price_ranges: []
  });
  const [cardFile, setCardFile] = useState(null);
  const [cardPreview, setCardPreview] = useState('');
  const [cardUrl, setCardUrl] = useState('');
  const [cardUploading, setCardUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const togglePriceRange = (label) => {
    setForm((f) => ({
      ...f,
      price_ranges: f.price_ranges.includes(label)
        ? f.price_ranges.filter((r) => r !== label)
        : [...f.price_ranges, label]
    }));
  };

  const handleCardSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image (JPG/PNG).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB.');
      return;
    }
    setCardFile(file);
    setCardPreview(URL.createObjectURL(file));

    try {
      setCardUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const resp = await api.post('/upload/image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCardUrl(resp.data.url);
      toast.success('Business card uploaded');
    } catch (err) {
      toast.error('Card upload failed');
      setCardFile(null);
      setCardPreview('');
    } finally {
      setCardUploading(false);
    }
  };

  const removeCard = () => {
    setCardFile(null);
    setCardPreview('');
    setCardUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim() || !form.contact_name.trim() || !form.phone.trim()) {
      toast.error('Please fill all required fields.');
      return;
    }
    if (form.price_ranges.length === 0) {
      toast.error('Please select at least one interested price range.');
      return;
    }
    try {
      setSubmitting(true);
      const origin = window.location.origin;
      const payload = {
        ...form,
        email: form.email.trim() || null,
        business_card_url: cardUrl ? (cardUrl.startsWith('/') ? `${origin}${cardUrl}` : cardUrl) : null
      };
      const resp = await api.post('/wholesale/inquiry', payload);
      setSubmitted(true);
      // Open WhatsApp in new tab so admin gets contact
      window.open(resp.data.whatsapp_url, '_blank');
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-24 md:py-32 max-w-2xl mx-auto px-4 md:px-8 text-center" data-testid="wholesale-success-page">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-10">
          <Check className="h-12 w-12 text-green-700 mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Inquiry Received
          </h1>
          <p className="text-muted-foreground mb-6">
            Thank you for your interest in wholesale. Our team has been notified and a WhatsApp chat has opened in a new tab. Continue the conversation there to receive our full wholesale catalog.
          </p>
          <a
            href="https://wa.me/917621003708"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-3 uppercase tracking-widest text-xs"
            data-testid="wholesale-continue-whatsapp"
          >
            <MessageCircle className="h-4 w-4" />
            Continue on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="py-24 md:py-32 max-w-[1200px] mx-auto px-4 md:px-8" data-testid="wholesale-page">
      {/* Hero */}
      <div className="text-center mb-16">
        <Handshake className="h-10 w-10 text-primary mx-auto mb-4" />
        <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Wholesale Partnership
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-light">
          Bring authentic Kutch Bandhani craftsmanship to your boutique, store, or online business. Direct from the artisan's studio at trade prices.
        </p>
      </div>

      {/* Terms */}
      <div className="mb-16 bg-secondary/50 border border-border rounded-2xl p-6 md:p-10" data-testid="wholesale-terms">
        <div className="flex items-center gap-3 mb-6">
          <Package className="h-6 w-6 text-primary" />
          <h2 className="text-2xl md:text-3xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Minimum Order Quantities
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICE_RANGES.map((r) => (
            <div key={r.id} className="bg-background border border-border rounded-xl p-5 text-center">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{r.label}</p>
              <p className="text-4xl font-light text-primary mb-1">{r.minPieces}</p>
              <p className="text-sm text-muted-foreground">pieces minimum</p>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-6 text-center">
          Each saree is entirely handcrafted by our master artisans and can take anywhere from 4 to 52 weeks to complete, depending on the intricacy of the design. Custom colours and patterns available on request.
        </p>
      </div>

      {/* Form */}
      <div className="border border-border rounded-2xl p-6 md:p-10">
        <h2 className="text-2xl md:text-3xl font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Get In Touch
        </h2>
        <p className="text-sm text-muted-foreground mb-8">
          Share your details and business card. We'll reach out on WhatsApp with our full catalog and pricing.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="wholesale-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ws-company" className="text-xs uppercase tracking-widest">Business / Store Name *</Label>
              <Input
                id="ws-company"
                name="company_name"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="rounded-full mt-2"
                required
                data-testid="wholesale-company-input"
              />
            </div>
            <div>
              <Label htmlFor="ws-contact" className="text-xs uppercase tracking-widest">Your Name *</Label>
              <Input
                id="ws-contact"
                name="contact_name"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="rounded-full mt-2"
                required
                data-testid="wholesale-contact-input"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ws-phone" className="text-xs uppercase tracking-widest">WhatsApp Phone *</Label>
              <Input
                id="ws-phone"
                name="phone"
                type="tel"
                placeholder="+91 XXXXXXXXXX"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="rounded-full mt-2"
                required
                data-testid="wholesale-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="ws-email" className="text-xs uppercase tracking-widest">Email (optional)</Label>
              <Input
                id="ws-email"
                name="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-full mt-2"
                data-testid="wholesale-email-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest mb-3 block">Interested Price Ranges *</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {PRICE_RANGES.map((r) => {
                const checked = form.price_ranges.includes(r.label);
                return (
                  <label
                    key={r.id}
                    className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition-all ${
                      checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                    }`}
                    data-testid={`wholesale-range-${r.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => togglePriceRange(r.label)}
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{r.label}</p>
                      <p className="text-xs text-muted-foreground mt-1">Min {r.minPieces} pieces</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest mb-3 block">Business Card (optional)</Label>
            {!cardPreview ? (
              <div>
                <input
                  type="file"
                  id="ws-card-file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleCardSelect}
                  data-testid="wholesale-card-input"
                />
                <label htmlFor="ws-card-file">
                  <Button type="button" variant="outline" className="rounded-full w-full" asChild disabled={cardUploading}>
                    <span className="cursor-pointer flex items-center justify-center gap-2">
                      {cardUploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> Upload Business Card</>}
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Adding your business card helps us verify your business faster.
                </p>
              </div>
            ) : (
              <div className="relative inline-block" data-testid="wholesale-card-preview">
                <img src={cardPreview} alt="Business card preview" className="max-w-xs h-40 object-cover rounded-xl border border-border" />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute -top-2 -right-2 rounded-full h-7 w-7"
                  onClick={removeCard}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="ws-message" className="text-xs uppercase tracking-widest">Additional Notes (optional)</Label>
            <textarea
              id="ws-message"
              name="message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full mt-2 rounded-xl p-4 border border-input bg-background min-h-[100px] text-sm"
              placeholder="Tell us about your business, expected order size, timeline…"
              maxLength={1000}
              data-testid="wholesale-message-input"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting || cardUploading}
            className="w-full rounded-full uppercase tracking-widest text-sm py-6 bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="wholesale-submit-btn"
          >
            {submitting ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>
            ) : (
              <><MessageCircle className="mr-2 h-4 w-4" /> Submit & Continue on WhatsApp</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Wholesale;
